import { Field } from '../../state/mint/actions'

const ADD_DRAFT_KEY = 'nm.liquidityAddFormDraft'
const REMOVE_DRAFT_KEY = 'nm.liquidityRemoveFormDraft'
const MAX_AGE_MS = 86_400_000

export type LiquidityAddFormDraft = {
  chainId: number
  token0: string
  token1: string
  independentField: Field
  typedValue: string
  otherTypedValue: string
  updatedAt: number
}

export type LiquidityRemoveFormDraft = {
  chainId: number
  token0: string
  token1: string
  liquidityPercent: string
  updatedAt: number
}

function readAddRaw(): LiquidityAddFormDraft | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(ADD_DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as LiquidityAddFormDraft
    if (!parsed?.chainId || !parsed.updatedAt) return null
    if (Date.now() - parsed.updatedAt > MAX_AGE_MS) {
      clearLiquidityAddFormDraft()
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function readRemoveRaw(): LiquidityRemoveFormDraft | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(REMOVE_DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as LiquidityRemoveFormDraft
    if (!parsed?.chainId || !parsed.updatedAt) return null
    if (Date.now() - parsed.updatedAt > MAX_AGE_MS) {
      clearLiquidityRemoveFormDraft()
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function readLiquidityAddFormDraft(chainId: number): LiquidityAddFormDraft | null {
  const draft = readAddRaw()
  if (!draft || draft.chainId !== chainId) return null
  return draft
}

export function persistLiquidityAddFormDraft(draft: Omit<LiquidityAddFormDraft, 'updatedAt'>): void {
  if (typeof window === 'undefined') return
  if (!draft.typedValue.trim() && !draft.otherTypedValue.trim()) return
  try {
    window.sessionStorage.setItem(
      ADD_DRAFT_KEY,
      JSON.stringify({ ...draft, updatedAt: Date.now() } satisfies LiquidityAddFormDraft),
    )
  } catch {
    /* quota */
  }
}

export function clearLiquidityAddFormDraft(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(ADD_DRAFT_KEY)
  } catch {
    /* ignore */
  }
}

export function hasLiquidityAddFormDraft(chainId?: number): boolean {
  const draft = readAddRaw()
  if (!draft) return false
  if (chainId !== undefined && draft.chainId !== chainId) return false
  return Boolean(draft.typedValue.trim() || draft.otherTypedValue.trim())
}

export function readLiquidityRemoveFormDraft(chainId: number): LiquidityRemoveFormDraft | null {
  const draft = readRemoveRaw()
  if (!draft || draft.chainId !== chainId) return null
  return draft
}

export function persistLiquidityRemoveFormDraft(draft: Omit<LiquidityRemoveFormDraft, 'updatedAt'>): void {
  if (typeof window === 'undefined') return
  if (!draft.liquidityPercent.trim() || draft.liquidityPercent === '0') return
  try {
    window.sessionStorage.setItem(
      REMOVE_DRAFT_KEY,
      JSON.stringify({ ...draft, updatedAt: Date.now() } satisfies LiquidityRemoveFormDraft),
    )
  } catch {
    /* quota */
  }
}

export function clearLiquidityRemoveFormDraft(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(REMOVE_DRAFT_KEY)
  } catch {
    /* ignore */
  }
}

export function hasLiquidityRemoveFormDraft(chainId?: number): boolean {
  const draft = readRemoveRaw()
  if (!draft) return false
  if (chainId !== undefined && draft.chainId !== chainId) return false
  return Boolean(draft.liquidityPercent.trim() && draft.liquidityPercent !== '0')
}
